import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import { useTheme } from "../../contexts/ThemeContext";
import { hexToRgba } from "../../utils/themes";

/** Labels + fills aligned for legend + chart. */
export const BAR_SEGMENT_META = [
  { key: "other", label: "Email" },
  { key: "pitches", label: "Pitch" },
  { key: "qualified", label: "ICP Match" },
];

export function useBarTrendPalette() {
  const { theme } = useTheme();
  const isLight = theme.mode === "light";
  const accent = theme.accent;
  return {
    other: isLight ? theme.borderBright : "#252C42",
    pitches: hexToRgba(accent, 0.5),
    qualified: accent,
    accent,
  };
}

const BAR_SEGMENT_LOOKUP = BAR_SEGMENT_META.reduce((acc, row) => {
  acc[row.key] = row.label;
  return acc;
}, {});

const ROUND = 7;

/** [topLeft, topRight, bottomRight, bottomLeft] per Recharts Rectangle convention */
function stackedRadii(layer, payload) {
  const o = Number(payload?.other) || 0;
  const p = Number(payload?.pitches) || 0;
  const q = Number(payload?.qualified) || 0;

  if (layer === "other") {
    const hasAbove = p > 0 || q > 0;
    const top = o > 0 && !hasAbove ? ROUND : 0;
    const bottom = o > 0 ? ROUND : 0;
    return [top, top, bottom, bottom];
  }
  if (layer === "pitches") {
    const hasBelow = o > 0;
    const hasAbove = q > 0;
    const top = p > 0 && !hasAbove ? ROUND : 0;
    const bottom = p > 0 && !hasBelow ? ROUND : 0;
    return [top, top, bottom, bottom];
  }
  if (layer === "qualified") {
    const hasBelow = o > 0 || p > 0;
    const top = q > 0 ? ROUND : 0;
    const bottom = q > 0 && !hasBelow ? ROUND : 0;
    return [top, top, bottom, bottom];
  }
  return [0, 0, 0, 0];
}

function clampCorner(r, width, height) {
  const cap = Math.min(Math.abs(width), Math.abs(height)) / 2;
  return Math.max(0, Math.min(r, cap || 0));
}

/** Asymmetric rounded rect; radii order [tl, tr, br, bl] */
function roundedRectPath(px, py, width, height, rawRadii) {
  const rtl = clampCorner(rawRadii[0], width, height);
  const rtr = clampCorner(rawRadii[1], width, height);
  const rbr = clampCorner(rawRadii[2], width, height);
  const rbl = clampCorner(rawRadii[3], width, height);
  const xr = px + width;
  const yb = py + height;

  let d = `M ${px + rtl},${py}`;
  d += ` H ${xr - rtr}`;
  if (rtr > 0) {
    d += ` A ${rtr} ${rtr} 0 0 1 ${xr},${py + rtr}`;
  }
  d += ` V ${yb - rbr}`;
  if (rbr > 0) {
    d += ` A ${rbr} ${rbr} 0 0 1 ${xr - rbr},${yb}`;
  }
  d += ` H ${px + rbl}`;
  if (rbl > 0) {
    d += ` A ${rbl} ${rbl} 0 0 1 ${px},${yb - rbl}`;
  }
  d += ` V ${py + rtl}`;
  if (rtl > 0) {
    d += ` A ${rtl} ${rtl} 0 0 1 ${px + rtl},${py}`;
  }
  return `${d} Z`;
}

function StackedBarPath({ layer, payload, fill, x, y, width, height, className, fillOpacity }) {
  if (width == null || height == null || height <= 0 || !payload) {
    return null;
  }
  const radii = stackedRadii(layer, payload);
  const d = roundedRectPath(Number(x), Number(y), Number(width), Number(height), radii);
  return <path d={d} fill={fill} className={className} fillOpacity={fillOpacity} />;
}

function barShape(layer) {
  return function Shape(props) {
    return <StackedBarPath {...props} layer={layer} />;
  };
}

const barShapeOther = barShape("other");
const barShapePitches = barShape("pitches");
const barShapeQualified = barShape("qualified");

const BarTrendChart = ({ data }) => {
  const { theme } = useTheme();
  const isLight = theme.mode === "light";
  const palette = useBarTrendPalette();
  const fillOther = palette.other;
  const fillPitches = palette.pitches;
  const fillQualified = palette.qualified;
  const gridStroke = isLight ? theme.border : "#1C2030";
  const axisStroke = isLight ? theme.muted : "#434A5C";
  const axisLine = isLight ? theme.border : "#1C2030";
  const tooltipBg = theme.secondary;
  const tooltipBorder = theme.border;
  const tooltipText = theme.text;
  const cursorFill = hexToRgba(palette.accent, 0.06);

  const n = data?.length ?? 0;
  const cramped = n > 20;
  const xInterval = cramped ? Math.max(Math.floor((n - 1) / 14), 1) : 0;

  return (
    <div className={`w-full ${cramped ? "h-[300px]" : "h-[260px]"}`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="day_label"
            stroke={axisStroke}
            tickLine={false}
            axisLine={{ stroke: axisLine }}
            tick={{ fontSize: cramped ? 9 : 11, fill: axisStroke }}
            interval={xInterval}
            {...(cramped ? { angle: -38, textAnchor: "end", height: 56 } : {})}
          />
          <YAxis
            stroke={axisStroke}
            tickLine={false}
            axisLine={{ stroke: axisLine }}
            tick={{ fill: axisStroke }}
          />
          <Tooltip
            cursor={{ fill: cursorFill }}
            contentStyle={{
              background: tooltipBg,
              border: `1px solid ${tooltipBorder}`,
              borderRadius: "8px",
              color: tooltipText,
              fontFamily: "JetBrains Mono, monospace",
              fontSize: "12px",
            }}
            formatter={(value, name) => [value, BAR_SEGMENT_LOOKUP[name] || name]}
          />
          <Bar dataKey="other" stackId="a" fill={fillOther} shape={barShapeOther} />
          <Bar dataKey="pitches" stackId="a" fill={fillPitches} shape={barShapePitches} />
          <Bar dataKey="qualified" stackId="a" fill={fillQualified} shape={barShapeQualified} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarTrendChart;
